import { cn } from '../../lib/utils';
import { ReflexMark } from '../brand/ReflexMark';

interface WizardLayoutProps {
  children: React.ReactNode;
  /**
   * Brand-row label rendered above the wizard body alongside the
   * Reflex mark (e.g. "Organization setup", "Reflex setup"). Omit for
   * loading / error states that don't need a header.
   */
  eyebrow?: React.ReactNode;
  /**
   * Right-aligned content for the brand row — typically an org switcher
   * during per-org bootstrap so a user with multiple memberships can
   * navigate out of the wizard at any time without getting trapped on
   * one org's setup flow. Only renders when an `eyebrow` is also set.
   */
  headerSlot?: React.ReactNode;
  className?: string;
  /**
   * When true, vertically centers the content in a flex container
   * instead of placing it in the top-anchored max-w-3xl column.
   * Use for short loading or error states.
   */
  centered?: boolean;
}

/**
 * Full-viewport shell used by the pre-app gate wizards (phase 1
 * `SetupPage`, phase 2 `OrgSetupPage`). Renders without the host
 * sidebar / `AppShell` so the wizard cannot be navigated away from
 * accidentally — users complete it or stay on it.
 *
 * When `eyebrow` is provided it renders as a brand row: the Reflex
 * tile mark followed by the eyebrow text styled as a compact header,
 * giving each wizard a clear product identity instead of a near-invisible
 * 2xs uppercase tag.
 */
export function WizardLayout({
  children,
  eyebrow,
  headerSlot,
  className,
  centered,
}: WizardLayoutProps) {
  if (centered) {
    return (
      <div
        className={cn('flex min-h-dvh items-center justify-center bg-background', className)}
        data-testid="wizard-layout"
      >
        {children}
      </div>
    );
  }
  return (
    <div className={cn('min-h-dvh bg-background', className)} data-testid="wizard-layout">
      <div className="mx-auto max-w-3xl px-6 py-12">
        {eyebrow && (
          <div
            className="mb-8 flex items-center justify-between gap-3"
            data-testid="wizard-layout-brand-header"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <ReflexMark variant="tile" className="h-8 w-8" />
              <span className="truncate text-lg font-semibold tracking-tight text-foreground">
                {eyebrow}
              </span>
            </div>
            {headerSlot && <div className="flex shrink-0 items-center">{headerSlot}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
